// Mock API service for development without backend
import { API_CONFIG } from '../config/apiConfig';
import { Company, UserRole } from '../types/api';

// Simulate network delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

interface MockUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string;
  role: UserRole;
  isVerified: boolean;
  createdAt: string;
}

interface MockJob {
  id: number;
  title: string;
  company: string;
  location: string;
  type: string;
  salary: { min: number; max: number };
  postedDate: string;
  description: string;
  skills: string[];
  isRemote: boolean;
  isUrgent: boolean;
  experience: string;
}

interface MockStorage {
  users: MockUser[];
  companies: Company[];
  jobs: MockJob[];
  applications: any[]; // Define a proper type for applications if needed
  tokens: Map<string, MockUser>;
}

// Mock data storage
const mockStorage = {
  users: [
    {
      id: 1,
      firstName: 'User',
      lastName: 'Demo',
      email: 'user@example.com',
      mobileNumber: '9876543210',
      role: 'Jobseeker',
      isVerified: true,
      createdAt: new Date().toISOString()
    },
    {
      id: 2,
      firstName: 'Agent',
      lastName: 'Recruiter',
      email: 'agent@example.com',
      mobileNumber: '9876543211',
      phone: '+919876543211',
      role: 'Agent',
      isVerified: true,
      createdAt: new Date().toISOString()
    },
    {
      id: 3,
      firstName: 'Company',
      lastName: 'Admin',
      email: 'company@example.com',
      mobileNumber: '9876543212',
      phone: '+919876543212',
      role: 'Company',
      isVerified: true,
      createdAt: new Date().toISOString()
    }
  ],
  companies: [],
  jobs: [
    {
      id: 1,
      title: 'Senior React Developer',
      company: 'TechCorp',
      location: 'San Francisco, CA',
      type: 'Full-time',
      salary: { min: 120000, max: 150000 },
      postedDate: '2024-01-15',
      description: 'We are looking for an experienced React developer to join our team...',
      skills: ['React', 'TypeScript', 'Node.js', 'AWS'],
      isRemote: true,
      isUrgent: false,
      experience: '5+ years'
    },
    {
      id: 2,
      title: 'Frontend Engineer',
      company: 'StartupXYZ',
      location: 'New York, NY',
      type: 'Full-time',
      salary: { min: 100000, max: 130000 },
      postedDate: '2024-01-14',
      description: 'Join our growing team as a Frontend Engineer...',
      skills: ['Vue.js', 'JavaScript', 'CSS', 'HTML'],
      isRemote: false,
      isUrgent: true,
      experience: '3+ years'
    }
  ],
  applications: [],
  tokens: new Map()
};

// Mock API responses
const mockApi = {
  // Auth endpoints
  async post(url, data) {
    await delay(500); // Simulate network delay
    
    if (API_CONFIG.DEBUG) {
      console.log(`[MOCK API] POST ${url}`, data);
    }

    switch (url) {
      case '/auth/register':
      case '/api/User/createUser':
        return this.handleRegister(data);
      
      case '/auth/login':
      case '/api/User/login':
        return this.handleLogin(data);
      
      case '/auth/verify-otp':
      case '/api/User/sendOTP':
        return this.handleSendOTP(data);
      
      case '/auth/setup-password':
        return this.handleSetupPassword(data);
      
      case '/auth/forgot-password':
        return this.handleForgotPassword(data);
      
      case '/auth/reset-password':
        return this.handleResetPassword(data);
      
      case '/auth/me':
        return this.handleGetMe();
      
      default:
        throw new Error(`Mock API: Endpoint ${url} not implemented`);
    }
  },

  async get(url) {
    await delay(300);
    
    if (API_CONFIG.DEBUG) {
      console.log(`[MOCK API] GET ${url}`);
    }

    switch (url) {
      case '/auth/me':
      case '/api/User/userDetails':
        return this.handleGetMe();
      
      case '/health':
        return { data: { status: 'ok', message: 'Mock API is running' } };
      
      case '/jobs':
        return { data: mockStorage.jobs };
      
      case '/companies':
        return { data: mockStorage.companies };
      
      default:
        throw new Error(`Mock API: Endpoint ${url} not implemented`);
    }
  },

  // Auth handlers
  handleRegister(data) {
    const { email, firstName, lastName, mobileNumber, role } = data;
    
    // Check if user already exists
    const existingUser = mockStorage.users.find(user => user.email === email);
    if (existingUser) {
      throw { response: { status: 409, data: { message: 'Email already exists' } } };
    }
    
    // Create new user
    const newUser = {
      id: mockStorage.users.length + 1,
      firstName,
      lastName,
      email,
      mobileNumber,
      role,
      isVerified: false,
      createdAt: new Date().toISOString()
    };
    
    mockStorage.users.push(newUser);
    
    // Generate mock OTP
    const otp = '123456';
    
    return {
      data: {
        message: 'Registration successful. Please verify your email.',
        user: newUser,
        otp: otp // In real app, this would be sent via email
      }
    };
  },

  handleLogin(data) {
    const { mobileNumber, password, otp } = data;
    
    // Find user by mobile number or email
    const user = mockStorage.users.find(u => 
      u.mobileNumber === mobileNumber || 
      u.email === mobileNumber ||
      u.phone === mobileNumber
    );
    
    if (!user) {
      throw { response: { status: 401, data: { message: 'Invalid mobile number' } } };
    }
    
    // Handle OTP login
    if (otp) {
      if (otp !== '123456') {
        throw { response: { status: 401, data: { message: 'Invalid OTP' } } };
      }
    } else {
      // Handle password login - for mock purposes, accept "password" for all users
      if (password !== 'password') {
        throw { response: { status: 401, data: { message: 'Invalid password' } } };
      }
    }
    
    // Generate mock token
    const token = `mock_token_${Date.now()}`;
    mockStorage.tokens.set(token, user);
    
    return {
      data: {
        status: 'success',
        data: {
          token,
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          role: (user.role || 'user').toLowerCase()
        }
      }
    };
  },

  handleSendOTP(data) {
    const { mobileNumber } = data;
    
    // Find user by mobile number
    const user = mockStorage.users.find(u => 
      u.mobileNumber === mobileNumber || 
      u.phone === mobileNumber
    );
    
    if (!user) {
      throw { response: { status: 404, data: { message: 'Mobile number not registered' } } };
    }
    
    return {
      data: {
        status: 'success',
        message: 'OTP sent successfully',
        otp: '123456' // Mock OTP for testing
      }
    };
  },

  handleVerifyOTP(data) {
    const { email, otp } = data;
    
    if (otp !== '123456') {
      throw { response: { status: 400, data: { message: 'Invalid OTP' } } };
    }
    
    const user = mockStorage.users.find(u => u.email === email);
    if (user) {
      user.isVerified = true;
    }
    
    return {
      data: {
        message: 'OTP verified successfully',
        user
      }
    };
  },

  handleSetupPassword(data) {
    const { email, password } = data;
    
    // For mock purposes, accept "Password" as the password
    if (password !== 'Password') {
      throw { response: { status: 400, data: { message: 'Password must be "Password"' } } };
    }
    
    // For mock purposes, create a user if they don't exist
    let user = mockStorage.users.find(u => u.email === email);
    if (!user) {
      // Create a new user for demo purposes
      user = {
        id: mockStorage.users.length + 1,
        firstName: 'Demo',
        lastName: 'User',
        email: email,
        mobileNumber: '+1234567890',
        role: 'Jobseeker',
        isVerified: true,
        createdAt: new Date().toISOString()
      };
      mockStorage.users.push(user);
    }
    
    // Generate mock token
    const token = `mock_token_${Date.now()}`;
    mockStorage.tokens.set(token, user);
    
    return {
      data: {
        token,
        user,
        message: 'Password setup successful'
      }
    };
  },

  handleForgotPassword(data) {
    const { email } = data;
    
    const user = mockStorage.users.find(u => u.email === email);
    if (!user) {
      throw { response: { status: 404, data: { message: 'User not found' } } };
    }
    
    return {
      data: {
        message: 'Password reset email sent',
        otp: '123456' // Mock OTP for testing
      }
    };
  },

  handleResetPassword(data) {
    const { email, otp, newPassword } = data;
    
    if (otp !== '123456') {
      throw { response: { status: 400, data: { message: 'Invalid OTP' } } };
    }
    
    const user = mockStorage.users.find(u => u.email === email);
    if (!user) {
      throw { response: { status: 404, data: { message: 'User not found' } } };
    }
    
    return {
      data: {
        message: 'Password reset successful'
      }
    };
  },

  handleGetMe() {
    // Normally, backend decodes the token and returns the associated user.
    // For the mock, read token from localStorage and look it up in tokens map.
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    let userFromToken = token ? mockStorage.tokens.get(token) : null;

    // If no token found, try to get user from localStorage (fallback)
    if (!userFromToken) {
      const storedUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (storedUser) {
        try {
          userFromToken = JSON.parse(storedUser);
        } catch (e) {
          console.error('Error parsing stored user:', e);
        }
      }
    }

    // Final fallback to first user (for legacy behavior) if token lookup fails
    if (!userFromToken) {
      userFromToken = mockStorage.users[0];
      if (!userFromToken) {
        userFromToken = {
          id: 1,
          firstName: 'Demo',
          lastName: 'User',
          email: 'demo@example.com',
          mobileNumber: '+1234567890',
          role: 'Jobseeker',
          isVerified: true,
          createdAt: new Date().toISOString()
        };
        mockStorage.users.push(userFromToken);
      }
    }

    return {
      data: userFromToken
    };
  }
};

export default mockApi;
